
import {
  Vue, Component, PropSync, Prop, Watch,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import { debounce } from 'ts-debounce';
import {
  PredictionAddressDto,
  ProvinceMetadata,
  CountryMetadataResponse,
  PayDetailsAddressDto,
  PaymentInfo,
} from '@/features/shipment/types';
import {
  getCarriersClarificationAddresses,
  getPayDetailsFromPlaceId,
  getCountryMetadata,
} from '@/features/shipment/services/shipment.services';
import {
  getPostalCodeRules,
} from '@/features/common/helpers/validationRules';
import { monerisEnabledCountryCodes } from '@/features/common/raw';
import { featureNames } from '@/features/common/constants/features';
import { getBooleanFeatureValue } from '@/features/common/helpers/feature-flag';
import CountrySelect from '@/features/common/components/CountrySelect.vue';

@Component({
  components: {
    CountrySelect,
  },
})
export default class PaymentAddressInput extends Vue {
  @PropSync('address', { required: true }) syncedAddress!: PaymentInfo['address'];

  @Prop({ required: true }) formSubmitted!: boolean;

  @Prop({ required: false, default: false }) onlyAddCard!: boolean;

  private showAddressDetails = false;

  private addressAutocomplete: {
    items: PredictionAddressDto[],
    isLoading: boolean,
    search: string,
  } = {
    items: [],
    isLoading: false,
    search: '',
  };

  private countryMetadata: CountryMetadataResponse | null = null;

  private isLoadingProvinceMetadata = false;

  private provinceMismatchDetected = false;

  private isSelectingAddress = false;

  private errorMessage = '';

  private isMonerisDirectIntegrationEnabled = false;

  get getConditionalRequiredRule(): InputValidationRules {
    return [
      (v: string): string | boolean => {
        if (!this.formSubmitted && !v) return true;
        return (!!v && v.toString().trim().length > 0) || this.$t('labels.required') as string;
      },
    ];
  }

  get allowedCountryCodes(): string[] | null {
    if (this.isMonerisDirectIntegrationEnabled) {
      return monerisEnabledCountryCodes;
    }
    return null;
  }

  get provinceOptions(): ProvinceMetadata[] {
    return this.countryMetadata?.province?.items || [];
  }

  get isProvinceRequired(): boolean {
    return this.countryMetadata?.province?.required === true;
  }

  get shouldShowProvinceField(): boolean {
    return this.countryMetadata?.province?.showField === true;
  }

  get singleProvinceValue(): string | null {
    if (this.countryMetadata?.province?.showField === false
      && this.countryMetadata.province.items
      && this.countryMetadata.province.items.length === 1) {
      return this.countryMetadata.province.items[0]['iso3166-2-code'];
    }
    return null;
  }

  get isProvinceDisabled(): boolean {
    if (!this.syncedAddress.countryCode) {
      return true;
    }

    if (this.isLoadingProvinceMetadata && this.provinceOptions.length === 0) {
      return true;
    }

    if (this.singleProvinceValue) {
      return true;
    }
    return false;
  }

  async mounted(): Promise<void> {
    const flagValue = await getBooleanFeatureValue(featureNames.useMonerisDirect3ds);
    this.isMonerisDirectIntegrationEnabled = flagValue === true || flagValue === undefined;
  }

  @Watch('addressAutocomplete.search', { immediate: false })
  private async searchAddresses(search: string) {
    if (this.isSelectingAddress) {
      return;
    }

    this.errorMessage = '';
    if (!search || search.trim().length === 0) {
      this.addressAutocomplete.items = [];
      return;
    }
    this.debounceSearchAddresses(search);
  }

  private debounceSearchAddresses = debounce(
    this.debounceSearchAddressesHandler, 300,
  );

  private async debounceSearchAddressesHandler(value: string) {
    if (!value || value.trim().length === 0) {
      this.addressAutocomplete.items = [];
      this.addressAutocomplete.isLoading = false;
      return;
    }

    this.addressAutocomplete.isLoading = true;
    try {
      const items = await getCarriersClarificationAddresses(value);
      this.addressAutocomplete.isLoading = false;
      if (!items) {
        this.addressAutocomplete.items = [];
        return;
      }

      this.addressAutocomplete.items = [...items];
    } catch (error) {
      this.addressAutocomplete.isLoading = false;
      this.addressAutocomplete.items = [];
    }
  }

  private resetAddressSelectionState() {
    this.addressAutocomplete.isLoading = false;
    this.isSelectingAddress = false;
  }

  private async onSelectAddress(address: PredictionAddressDto | string | null) {
    if (!address || typeof address === 'string') {
      return;
    }

    const { placeId } = address;
    if (!placeId) return;

    this.isSelectingAddress = true;
    this.addressAutocomplete.isLoading = true;
    this.clearAddressFields();
    this.errorMessage = '';

    let addressDetail: PayDetailsAddressDto[] | null;
    try {
      addressDetail = await getPayDetailsFromPlaceId(placeId);
    } catch (error) {
      this.resetAddressSelectionState();
      return;
    }

    if (!addressDetail || !Array.isArray(addressDetail) || addressDetail.length === 0) {
      this.resetAddressSelectionState();
      return;
    }

    const addressData = addressDetail[0];
    const {
      countryCode, postalCode, city, addr1, province,
    } = addressData;

    if (!countryCode) {
      this.resetAddressSelectionState();
      return;
    }

    const countrySupported = await this.loadCountryMetadata(countryCode);

    if (!countrySupported) {
      this.resetAddressSelectionState();
      return;
    }

    this.syncedAddress.addr1 = addr1 || '';
    this.syncedAddress.countryCode = countryCode || '';
    this.syncedAddress.postalCode = postalCode || '';
    this.syncedAddress.city = city || '';

    await this.handleProvinceFromLookup(province);

    this.showAddressDetails = true;
    await this.$nextTick();
    this.resetAddressSelectionState();
  }

  private clearAddressFields() {
    this.syncedAddress.addr1 = '';
    this.syncedAddress.countryCode = '';
    this.syncedAddress.postalCode = '';
    this.syncedAddress.city = '';
    this.syncedAddress.province = '';
  }

  private showManualEntry() {
    this.errorMessage = '';
    this.addressAutocomplete.search = '';
    this.addressAutocomplete.items = [];
    this.clearAddressFields();
    this.showAddressDetails = true;

    if (this.syncedAddress.countryCode) {
      this.loadCountryMetadata(this.syncedAddress.countryCode);
    }
  }

  private async loadCountryMetadata(countryCode: string): Promise<boolean> {
    this.errorMessage = '';

    if (!countryCode) {
      return false;
    }

    this.isLoadingProvinceMetadata = true;

    try {
      const metadata = await getCountryMetadata(countryCode);
      if (!metadata || Object.keys(metadata).length === 0) {
        this.countryMetadata = null;
        this.errorMessage = this.$t('checkout.countryNotSupported') as string;
        return false;
      }

      this.countryMetadata = metadata;

      const singleProvince = this.singleProvinceValue;
      if (singleProvince !== null) {
        this.syncedAddress.province = singleProvince;
      }

      return true;
    } catch (error) {
      this.countryMetadata = null;
      this.errorMessage = this.$t('checkout.countryNotSupported') as string;
      return false;
    } finally {
      this.isLoadingProvinceMetadata = false;
    }
  }

  private async handleProvinceFromLookup(provinceFromLookup?:
  { code?: string; displayName?: string }) {
    if (!this.countryMetadata) {
      this.syncedAddress.province = '';
      this.provinceMismatchDetected = false;
      return;
    }

    const singleProvince = this.singleProvinceValue;
    if (singleProvince !== null) {
      this.syncedAddress.province = singleProvince;
      this.provinceMismatchDetected = false;
      return;
    }

    if (!provinceFromLookup) {
      this.syncedAddress.province = '';
      this.provinceMismatchDetected = false;
      return;
    }

    const provinceCode = provinceFromLookup.code;

    if (!provinceCode) {
      this.syncedAddress.province = '';
      this.provinceMismatchDetected = false;
      return;
    }

    const matchingProvince = this.provinceOptions.find(
      (p) => p['iso3166-2-code']?.toLowerCase() === provinceCode.toLowerCase(),
    );

    if (matchingProvince) {
      this.syncedAddress.province = matchingProvince['iso3166-2-code'];
      this.provinceMismatchDetected = false;
    } else {
      this.provinceMismatchDetected = true;
      this.syncedAddress.province = '';
    }
  }

  private async onCountryChange() {
    const { countryCode } = this.syncedAddress;
    if (countryCode) {
      this.errorMessage = '';
      this.syncedAddress.province = '';
      this.provinceMismatchDetected = false;
      await this.loadCountryMetadata(countryCode);
    }
  }

  private trimPostalCode() {
    this.syncedAddress.postalCode = this.syncedAddress.postalCode.trim();
  }

  private handlePostalCodeInput(val: string) {
    this.syncedAddress.postalCode = val && val.toUpperCase();
  }

  private async handlePostalCodeFocusOut() {
    this.trimPostalCode();
  }

  private getCityValidationRules(): InputValidationRules {
    if (!this.showAddressDetails && !this.formSubmitted) {
      return [];
    }

    return [
      (v: string): string | boolean => {
        if (this.showAddressDetails && (!v || !v.trim())) {
          return this.$t('labels.required') as string;
        }
        return true;
      },
    ];
  }

  private getProvinceValidationRules(): InputValidationRules {
    if (!this.showAddressDetails && !this.formSubmitted) {
      return [];
    }

    if (!this.shouldShowProvinceField) {
      return [];
    }

    const needsProvince = this.isProvinceRequired || this.provinceMismatchDetected;

    if (!needsProvince && !this.formSubmitted) {
      return [];
    }

    return [
      (v: string): string | boolean => {
        if (needsProvince && (!v || !v.trim())) {
          return this.$t('labels.required') as string;
        }
        return true;
      },
    ];
  }

  private getPostalCodeRules(): InputValidationRules {
    if (!this.formSubmitted && !this.syncedAddress.postalCode) return [];

    const { countryCode } = this.syncedAddress;
    const countryInfo = this.$store.getters.getCountryFromCode(countryCode);

    if (!countryInfo) {
      return this.getConditionalRequiredRule;
    }

    return getPostalCodeRules(countryInfo);
  }

  public async ensureAddressDetailsVisible(): Promise<void> {
    if (!this.showAddressDetails) {
      const hasMissingFields = this.checkForMissingAddressFields();
      if (hasMissingFields || this.errorMessage) {
        this.showManualEntry();
      }
    }
  }

  private checkForMissingAddressFields(): boolean {
    const {
      addr1, countryCode, postalCode, city,
    } = this.syncedAddress;
    return !addr1 || !addr1.trim()
    || !countryCode || !postalCode || !postalCode.trim() || !city || !city.trim();
  }
}
